import type { Schemas } from "../api/client";
import { useMe } from "./auth";

export type Units = Schemas["Units"];

// Everything is stored in kg and cm; these only change what people see and type.
export const KG_PER_LB = 0.45359237;
export const CM_PER_INCH = 2.54;

export function useUnits(): Units {
  return useMe().data?.units ?? "metric";
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** A weight in the user's units, as a number (for inputs and charts). */
export function weightIn(kg: number, units: Units): number {
  return round1(units === "imperial" ? kg / KG_PER_LB : kg);
}

export function weightToKg(value: number, units: Units): number {
  return units === "imperial" ? value * KG_PER_LB : value;
}

export function weightUnit(units: Units): string {
  return units === "imperial" ? "lb" : "kg";
}

export function formatWeight(kg: number | null | undefined, units: Units): string {
  return kg == null ? "–" : `${weightIn(kg, units)} ${weightUnit(units)}`;
}

export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const total = Math.round(cm / CM_PER_INCH);
  return { feet: Math.floor(total / 12), inches: total % 12 };
}

export function feetInchesToCm(feet: number, inches: number): number {
  return round1((feet * 12 + inches) * CM_PER_INCH);
}

// Weekly weight-change goals, in the units people think in. The API always takes kg/week.
const PACES: Record<Units, number[]> = {
  metric: [-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5],
  imperial: [-2, -1.5, -1, -0.5, 0, 0.5, 1],
};
export const DEFAULT_PACE: Record<Units, string> = { metric: "-0.5", imperial: "-1" };

export function paceOptions(units: Units): { value: string; label: string }[] {
  const unit = units === "imperial" ? "lb" : "kg";
  return PACES[units].map((p) => ({
    value: String(p),
    label: p === 0 ? "Maintain" : `${p < 0 ? "Lose" : "Gain"} ${Math.abs(p)} ${unit}/week`,
  }));
}

/** A pace chosen in the user's units, as kg per week for the API. */
export function paceToKgPerWeek(pace: number, units: Units): number {
  return weightToKg(pace, units);
}
